import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Globe, GripVertical, Lock, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/auth/AuthProvider";
import { GenieActionModal, GenieModals } from "@/components/agent/core";
import { GenieAvatar } from "@/components/GenieAvatar";
import { GenieChannelIndicators } from "@/components/agent/GenieChannelIndicators";
import { cn } from "@/lib/utils";
import { Agent } from "@/types/agent/agent";
import { AgentGroup } from "@/types/agent/agent-group";
import { getGenieTypeMeta } from "@/types/genie-type";
import { DEFAULT_BRANDING } from "@/types/ui/branding";
import { resolveGenieAvatarUrl } from "@/utils/image/genieAvatars";
import { GroupSelector } from "./GroupSelector";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

interface MagicGenieCardProps {
  genie: Agent;
  groups?: AgentGroup[];
  isDragEnabled?: boolean;
  isReordering?: boolean;
  /** Conversations in the trailing 7-day window. `undefined` = not yet loaded. */
  weeklyChats?: number;
}

/**
 * My Genies card — quiet chips, avatar, name, and three always-visible actions
 * for the jobs an owner actually does: Share, Knowledge, Settings. Share opens
 * the GenieShareDrawer — that's where sharing actually happens (links, QR, team
 * invites). Settings opens the same immersive workspace as the "Open" door
 * (`/genie-hub/my-genies/:id`) — there is only one settings view.
 */
const MagicGenieCard = ({ genie, groups = [], isDragEnabled = false, isReordering = false, weeklyChats }: MagicGenieCardProps) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeModal, setActiveModal] = useState<GenieActionModal | null>(null);

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: genie.id,
    disabled: !isDragEnabled,
  });

  const brand = genie.branding?.primaryColor || DEFAULT_BRANDING.primaryColor;
  const secondary = genie.branding?.secondaryColor;
  const hasBranding: boolean = Boolean(
    genie.branding &&
      (genie.branding.primaryColor !== DEFAULT_BRANDING.primaryColor ||
        genie.branding.secondaryColor ||
        genie.branding.customAvatarUrl ||
        genie.branding.gradientEnabled),
  );

  const isTeamGenie = user?.id && genie.user_id !== user.id;
  const urlName = genie.url_name ?? genie.page?.url_name;
  const typeMeta = getGenieTypeMeta(genie.type);

  // Brand gradient for the avatar hover + primary CTA.
  const brandGradient =
    genie.branding?.gradientEnabled && secondary
      ? `linear-gradient(${genie.branding.gradientAngle || 135}deg, ${brand}, ${secondary})`
      : brand;

  const hasActivitySignal = typeof weeklyChats === "number";

  const openLive = () => {
    if (!urlName) return toast.error("Unable to open live page");
    navigate(`/live-agent/${urlName}`);
  };

  const handleShare = () => {
    if (!urlName) {
      toast.error("Unable to generate share link");
      return;
    }
    setActiveModal("shareDrawer");
  };

  const statusPill = !genie.is_active
    ? { dot: "#9CA3AF", text: "Inactive", tone: "text-gray-500 bg-gray-100" }
    : hasActivitySignal && weeklyChats! > 0
      ? { dot: brand, text: `Live · ${weeklyChats} chats this week`, tone: "text-gray-700 bg-gray-50" }
      : hasActivitySignal
        ? { dot: brand, text: "Live · Ready to share", tone: "text-gray-600 bg-gray-50" }
        : { dot: brand, text: "Live", tone: "text-gray-600 bg-gray-50" };

  const quickActions = [
    { label: "Share", genieIcon: "share" as const, onClick: handleShare },
    { label: "Knowledge", genieIcon: "book-open" as const, onClick: () => setActiveModal("knowledge") },
    { label: "Settings", genieIcon: "settings" as const, onClick: () => navigate(`/genie-hub/my-genies/${genie.id}`) },
  ];

  return (
    <>
      <div
        ref={setNodeRef}
        className="bg-white rounded-3xl shadow-lg border-2 border-gray-100 flex flex-col overflow-hidden transition-all duration-300 hover:shadow-2xl"
        style={{
          transform: CSS.Transform.toString(transform),
          transition,
          opacity: isDragging ? 0.5 : 1,
          ...(hasBranding
            ? { borderColor: brand, boxShadow: `0 10px 15px -3px ${brand}20, 0 4px 6px -2px ${brand}10` }
            : {}),
        }}
      >
        {/* Brand accent — gradient strip across the top, matching the primary CTA.
            Clipped flush to the card's rounded corners by the parent's overflow-hidden. */}
        <div className="h-2 shrink-0" style={{ background: brandGradient }} />

        {/* Quiet chip row */}
        <div className="flex items-center gap-2 px-4 pt-3 flex-wrap">
          <button
            {...attributes}
            {...listeners}
            className="p-1 -ml-1 text-gray-300 hover:text-gray-500 rounded transition-colors cursor-grab active:cursor-grabbing"
            title="Drag to reorder"
          >
            <GripVertical className="w-4 h-4" />
          </button>

          {/* Icon-only on mobile — the words crowd the chip row at phone width. */}
          <span
            aria-label={genie.is_public ? "Public" : "Private"}
            title={genie.is_public ? "Public" : "Private"}
            className={cn(
              "inline-flex items-center gap-1.5 px-1.5 sm:px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wider ring-1",
              genie.is_public
                ? "bg-genie-primary-light text-genie-primary-dark ring-genie-primary/30"
                : "bg-genie-accent-light text-genie-accent-dark ring-genie-accent/40",
            )}
          >
            {genie.is_public ? <Globe className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
            <span className="hidden sm:inline">{genie.is_public ? "Public" : "Private"}</span>
          </span>

          <span
            aria-label={typeMeta.label}
            title={typeMeta.label}
            className="inline-flex items-center gap-1.5 px-1.5 sm:px-2.5 py-1 rounded-full text-xs font-medium ring-1"
            style={{ color: brand, backgroundColor: `${brand}10`, ["--tw-ring-color" as string]: `${brand}40` }}
          >
            <typeMeta.icon className="w-3 h-3" />
            <span className="hidden sm:inline">{typeMeta.label}</span>
          </span>

          {isTeamGenie && (
            <span className="px-2 py-1 rounded-full text-xs font-medium text-gray-700 bg-gray-100">Team</span>
          )}

          {(genie.group || groups.length > 0) && (
            <GroupSelector
              agentId={genie.id}
              currentGroupId={genie.group_id}
              groups={groups}
              currentGroup={genie.group}
              agent={genie}
              disabled={isReordering}
              brandColor={brand}
              secondaryColor={genie.branding?.secondaryColor}
              gradientEnabled={genie.branding?.gradientEnabled}
              gradientAngle={genie.branding?.gradientAngle}
            />
          )}

          {/* Step inside the Genie — the one door into its immersive workspace
              (setup → activate), all within the loop. Expand arrows + a twinkle. */}
          <button
            type="button"
            onClick={() => navigate(`/genie-hub/my-genies/${genie.id}`)}
            className="group ml-auto relative inline-flex items-center gap-1.5 h-8 pl-3 pr-3.5 rounded-full text-white text-xs font-bold shadow-sm hover:shadow-md hover:scale-[1.04] transition-all"
            style={{ background: brandGradient }}
            title="Open this Genie's workspace"
          >
            {/* twinkle accent */}
            <span className="pointer-events-none absolute -top-1 -right-1 text-white animate-twinkle">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                <path d="M12 0l2.6 9.4L24 12l-9.4 2.6L12 24l-2.6-9.4L0 12l9.4-2.6z" />
              </svg>
            </span>
            {/* expand — two arrows, diagonally opposed */}
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="transition-transform group-hover:scale-110"
              aria-hidden
            >
              <polyline points="15 3 21 3 21 9" />
              <polyline points="9 21 3 21 3 15" />
              <line x1="21" y1="3" x2="14" y2="10" />
              <line x1="3" y1="21" x2="10" y2="14" />
            </svg>
            Open
          </button>
        </div>

        {/* Body */}
        <div className="px-6 pt-2 pb-6 flex-1 flex flex-col items-center text-center">
          {/* Avatar — click to open the live Genie. The pill straddling the
              bottom edge is an always-visible, touch-friendly cue: the old
              corner badge was hover-only, so it never showed on iPad/touch. */}
          <button
            type="button"
            onClick={openLive}
            className="group relative mb-6 transform scale-90"
            title={genie.is_active ? "Chat live with this Genie" : "Preview this Genie"}
            aria-label={genie.is_active ? `Chat live with ${genie.name}` : `Preview ${genie.name}`}
          >
            <div className={cn(!genie.is_active && "grayscale opacity-60")}>
              <GenieAvatar
                color={brand}
                secondaryColor={secondary}
                gradientEnabled={genie.branding?.gradientEnabled}
                gradientAngle={genie.branding?.gradientAngle}
                avatarBackgroundOpacity={genie.branding?.avatarBackgroundOpacity}
                speaking={false}
                listening={false}
                isConnecting={false}
                customAvatarUrl={resolveGenieAvatarUrl(genie)}
                hasCustomBranding={hasBranding}
              />
            </div>
            <span className="absolute left-1/2 -translate-x-1/2 bottom-0 translate-y-1/2 z-30">
              <span
                className="inline-flex items-center gap-1.5 pl-2.5 pr-3 py-1.5 rounded-full text-white text-xs font-bold whitespace-nowrap shadow-lg ring-2 ring-white animate-bounce-subtle group-hover:shadow-xl transition-shadow"
                style={{ background: brandGradient }}
              >
                <MessageCircle className="w-3.5 h-3.5" />
                {genie.is_active ? "Tap to chat" : "Tap to preview"}
              </span>
            </span>
          </button>

          <h3 className="text-2xl font-bold text-gray-900 mb-1.5">{genie.name}</h3>
          <p className="text-gray-600 text-sm mb-3 px-2 line-clamp-2 min-h-[2.5rem]">
            {(genie.welcome_message ?? genie.page?.welcome_message) || genie.description || "No description available"}
          </p>

          {/* Adaptive status line + telephony at a glance */}
          <div className="flex flex-wrap items-center justify-center gap-1.5 mb-4">
            <span
              className={cn(
                "inline-flex items-center gap-2 h-7 px-3 rounded-full text-xs font-medium",
                statusPill.tone,
              )}
            >
              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: statusPill.dot }} />
              {statusPill.text}
            </span>
            <GenieChannelIndicators genie={genie} brand={brand} className="justify-center" />
          </div>

          {/* Always-visible quick actions — the three jobs an owner actually does. */}
          <div className="w-full grid grid-cols-3 gap-2">
            {quickActions.map((action) => (
              <button
                key={action.label}
                onClick={action.onClick}
                className="flex flex-col items-center gap-1.5 py-2.5 px-1 rounded-2xl transition-colors hover:bg-gray-50"
                style={{ backgroundColor: `${brand}0A` }}
              >
                <GenieIcon
                  name={action.genieIcon}
                  size={26}
                  color={brand}
                  secondaryColor={secondary || undefined}
                  gradientEnabled={!!genie.branding?.gradientEnabled}
                  gradientAngle={genie.branding?.gradientAngle || 135}
                  label={action.label}
                />
                <span className="text-xs text-gray-600">{action.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <GenieModals genie={genie} activeModal={activeModal} onClose={() => setActiveModal(null)} onSwitchModal={setActiveModal} />
    </>
  );
};

export default MagicGenieCard;
